import React, { useEffect, useState } from 'react';
import { Typography, Grid, Box, Container, CircularProgress } from '@mui/material';
import {
	AttachMoney as RevenueIcon,
	ShoppingCartCheckout as OrderIcon,
	Inventory as InventoryIcon
} from '@mui/icons-material';

// Components
import KPICard from '../components/dashboard/KPICard';
import QuickActions from '../components/dashboard/QuickActions';
import AlertsFeed from '../components/dashboard/AlertsFeed';
import { RevenueWidget } from '../components/widgets/RevenueWidget';
import { RetentionWidget } from '../components/widgets/RetentionWidget';

import LowStockAlertWidget from '../components/dashboard/LowStockAlertWidget';
import TopCustomersWidget from '../components/dashboard/TopCustomersWidget';
import InventoryAlertsWidget from '../components/dashboard/InventoryAlertsWidget';
import { GlobalInventoryMatrix } from '../components/dashboard/GlobalInventoryMatrix';
import { SystemStatusWidget } from '../components/dashboard/SystemStatusWidget';
import HolyTrinityTriageQueue from '../components/triage/HolyTrinityTriageQueue';

// Services
import { reportService } from '../services/reportService';
import { fetchProductStats } from '../services/productService';
import { formatCurrency } from '../utils/currency';

const Dashboard: React.FC = () => {
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState({
		totalRevenue: 0,
		activeOrders: 0, // Pending + Processing
		lowStock: 0,
		totalOrders: 0
	});

	useEffect(() => {
		const loadDashboardData = async () => {
			try {
				const [reportStats, productStats] = await Promise.all([
					reportService.getDashboardStats(),
					fetchProductStats()
				]);

				setStats({
					totalRevenue: reportStats.revenue?.value || 0,
					activeOrders: reportStats.pendingOrders || 0,
					lowStock: reportStats.lowStock || productStats.lowStock || 0, // Fallback to productStats if needed
					totalOrders: reportStats.orders?.value || 0
				});
			} catch (error) {
				console.error('Failed to load dashboard data', error);
			} finally {
				setLoading(false);
			}
		};

		loadDashboardData();
	}, []);

	return (
		<Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
			<InventoryAlertsWidget />
			{/* --- TOP ROW: Activity Pulse --- */}
			<Box sx={{ mb: 4 }}>
				<Typography variant="h4" fontWeight="bold" gutterBottom>
					Command Center
				</Typography>
				<Typography variant="body1" color="text.secondary">
					Real-time overview of business performance and system health.
				</Typography>
			</Box>


			<Grid container spacing={3}>
				{/* SECTION 0: System Status */}
				<Grid item xs={12}>
					<SystemStatusWidget />
				</Grid>

				{/* SECTION 0.5: Shadow Mode Flashing Triage Queue (Holy Trinity UI) */}
				<Grid item xs={12}>
					<HolyTrinityTriageQueue />
				</Grid>

				{/* SECTION 0.8: EPIC-INV-001 Multi-Facility Global Oversight Cockpit */}
				<Grid item xs={12}>
					<GlobalInventoryMatrix />
				</Grid>

				{/* SECTION 1: HUD (Heads Up Display) */}
				<Grid item xs={12} md={4}>
					<KPICard
						title="Total Revenue"
						value={formatCurrency(stats.totalRevenue)}
						icon={<RevenueIcon fontSize="large" />}
						color="#10b981" // Emerald-500
						loading={loading}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<KPICard
						title="Pending Orders"
						value={stats.activeOrders}
						subValue="Requiring Fulfillment"
						icon={<OrderIcon fontSize="large" />}
						color="#f59e0b" // Amber-500
						loading={loading}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<KPICard
						title="Low Stock Alerts"
						value={stats.lowStock}
						subValue="Products below threshold"
						icon={<InventoryIcon fontSize="large" />}
						color="#ef4444" // Red-500
						loading={loading}
					/>
				</Grid>

				{/* SECTION 2: Action & Alert Bar */}
				<Grid item xs={12} md={8}>
					<QuickActions />
				</Grid>
				<Grid item xs={12} md={4}>
					<AlertsFeed
						pendingOrders={stats.activeOrders}
						lowStockCount={stats.lowStock}
						loading={loading}
					/>
				</Grid>


				{/* SECTION 3: Deep Dive (Enterprise Analytics) */}
				<Grid item xs={12} lg={8}>
					<RevenueWidget />
				</Grid>
				<Grid item xs={12} lg={4}>
					<RetentionWidget />
				</Grid>

				<Grid item xs={12}>
					<TopCustomersWidget />
				</Grid>
			</Grid>
		</Container>
	);
};

export default Dashboard;
